"use client";

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

export interface SSELocation {
  lat: number;
  lng: number;
}

export interface SSEAgent {
  ulid?: string;
  name?: string;
  phone?: string;
}

export interface OrderSSEState {
  /** Live status from SSE, null = not yet received */
  status: string | null;
  /** Rider's current GPS location */
  location: SSELocation | null;
  /** Assigned rider/agent info */
  agent: SSEAgent | null;
  /** Whether the SSE connection is open */
  connected: boolean;
  /** Raw last event data for debugging / future fields */
  lastEvent: unknown;
}

const TERMINAL_STATUSES = new Set([
  "delivered",
  "completed",
  "cancelled",
  "failed",
]);

/**
 * Opens an SSE connection to track a specific order in real-time.
 * Automatically stops when the order reaches a terminal status.
 */
export function useOrderSSE(orderUlid: string | null, initialStatus?: string): OrderSSEState {
  const [state, setState] = useState<OrderSSEState>({
    status: null,
    location: null,
    agent: null,
    connected: false,
    lastEvent: null,
  });

  const esRef = useRef<EventSource | null>(null);


  useEffect(() => {
    // Don't connect if no ulid or order is already in a terminal state
    if (!orderUlid) return;
    if (initialStatus && TERMINAL_STATUSES.has(initialStatus)) return;

    const es = new EventSource(`/api/sse/orders/${orderUlid}`);
    esRef.current = es;

    es.onopen = () => {
      setState((prev) => ({ ...prev, connected: true }));
    };

    function handleEvent(event: MessageEvent) {
      try {
        const data = JSON.parse(event.data);

        // Skip pure keep-alive frames — nothing to update
        if (data.type === "heartbeat" || data.type === "connected") return;

        setState((prev) => {
          // Extract location from various possible field layouts
          let location = prev.location;
          const loc =
            data.location ??
            data.agent_location ??
            data.driver_location ??
            data.coordinates ??
            null;
          if (loc) {
            let lat: number;
            let lng: number;
            if (loc.type === "Point" && Array.isArray(loc.coordinates)) {
              // GeoJSON Point: [longitude, latitude]
              lng = Number(loc.coordinates[0]);
              lat = Number(loc.coordinates[1]);
            } else {
              lat = Number(loc.latitude ?? loc.lat ?? 0);
              lng = Number(loc.longitude ?? loc.lng ?? loc.lon ?? 0);
            }
            if (lat && lng) location = { lat, lng };
          } else {
            // Top-level coordinates: covers {"type":"location","lat":...,"lng":...}
            const lat = Number(data.latitude ?? data.lat ?? 0);
            const lng = Number(data.longitude ?? data.lng ?? data.lon ?? 0);
            if (lat && lng) location = { lat, lng };
          }

          // Extract agent info
          const agentRaw = data.agent ?? data.driver ?? null;
          const agent: SSEAgent | null = agentRaw
            ? {
                ulid: agentRaw.ulid ?? agentRaw.id,
                name: agentRaw.name ?? agentRaw.full_name,
                phone: agentRaw.phone ?? agentRaw.phone_number,
              }
            : prev.agent;

          // Extract status
          const status =
            data.status ??
            data.order?.status ??
            data.data?.status ??
            prev.status;

          return { ...prev, status, location, agent, lastEvent: data };
        });
      } catch {
        // Ignore non-JSON heartbeats / comments
      }
    }

    // Listen on default message event AND all named event types the server may emit
    es.onmessage = handleEvent;
    const namedEvents = [
      "order_update", "order.update",
      "location", "driver_location", "agent_location", "agentLocationUpdate",
      "status", "status_update",
      "driver_assigned", "agent_assigned",
      "update",
    ];
    for (const name of namedEvents) {
      es.addEventListener(name, handleEvent);
    }

    es.onerror = () => {
      setState((prev) => ({ ...prev, connected: false }));
      // EventSource will auto-retry; no manual reconnect needed
    };

    return () => {
      for (const name of namedEvents) {
        es.removeEventListener(name, handleEvent);
      }
      es.close();
      esRef.current = null;
      setState((prev) => ({ ...prev, connected: false }));
    };
  }, [orderUlid, initialStatus]);

  // Close once order is delivered/cancelled
  useEffect(() => {
    if (state.status && TERMINAL_STATUSES.has(state.status)) {
      esRef.current?.close();
      esRef.current = null;
    }
  }, [state.status]);

  return state;
}

/**
 * Opens an SSE connection that streams ALL ongoing orders for the current customer.
 * Returns a map of orderUlid → { status, location }.
 */
export function useCustomerOrdersSSE(enabled: boolean): Record<string, OrderSSEState> {
  const [orders, setOrders] = useState<Record<string, OrderSSEState>>({});
  const queryClient = useQueryClient();
  // Last-seen status per order, so we only refetch the REST "ongoing" list when
  // a change actually affects its membership (new order, or a status change such
  // as delivered/cancelled) — never on plain location pings.
  const lastStatusRef = useRef<Record<string, string | null>>({});

  useEffect(() => {
    if (!enabled) return;

    // Parse one raw SSE payload: refresh the ongoing list on real changes, then
    // merge status/location into local state. Shared by the SharedWorker and the
    // direct-EventSource fallback paths below.
    const handleData = (raw: string) => {
      try {
        const data = JSON.parse(raw);
        if (data.type === "heartbeat" || data.type === "connected") return;
        const ulid: string | undefined =
          data.order_ulid ?? data.ulid ?? data.order?.ulid;
        if (!ulid) return;

        // Refetch the ongoing list once when this order is new to us or its
        // status moved. React Query collapses concurrent invalidations.
        const nextStatus: string | null = data.status ?? data.order?.status ?? null;
        const isNew = !(ulid in lastStatusRef.current);
        const statusChanged = nextStatus != null && lastStatusRef.current[ulid] !== nextStatus;
        if (isNew || statusChanged) {
          lastStatusRef.current[ulid] = nextStatus;
          queryClient.invalidateQueries({ queryKey: ["orders", "ongoing"] });
        }

        setOrders((prev) => {
          const existing = prev[ulid] ?? {
            status: null,
            location: null,
            agent: null,
            connected: true,
            lastEvent: null,
          };

          const loc = data.location ?? data.agent_location ?? data.driver_location ?? null;
          let location = existing.location;
          if (loc) {
            let lat: number;
            let lng: number;
            if (loc.type === "Point" && Array.isArray(loc.coordinates)) {
              lng = Number(loc.coordinates[0]);
              lat = Number(loc.coordinates[1]);
            } else {
              lat = Number(loc.latitude ?? loc.lat ?? 0);
              lng = Number(loc.longitude ?? loc.lng ?? loc.lon ?? 0);
            }
            if (lat && lng) location = { lat, lng };
          } else {
            const lat = Number(data.latitude ?? data.lat ?? 0);
            const lng = Number(data.longitude ?? data.lng ?? data.lon ?? 0);
            if (lat && lng) location = { lat, lng };
          }

          const status = data.status ?? data.order?.status ?? existing.status;
          return {
            ...prev,
            [ulid]: { ...existing, status, location, lastEvent: data },
          };
        });
      } catch {
        // ignore
      }
    };

    // Preferred: one SharedWorker holds a single SSE connection shared across
    // every page and tab. Falls back to a direct EventSource where SharedWorker
    // is unavailable (e.g. some mobile browsers).
    if (typeof SharedWorker !== "undefined") {
      const worker = new SharedWorker("/sse-worker.js", { name: "fast-orders-sse" });
      const port = worker.port;
      port.onmessage = (ev: MessageEvent) => {
        const msg = ev.data;
        if (msg && msg.kind === "message" && typeof msg.data === "string") {
          handleData(msg.data);
        }
      };
      port.start();

      // Detach this page from the shared connection on unmount / navigation.
      const detach = () => {
        try { port.postMessage({ type: "close" }); } catch { /* port already closed */ }
      };
      window.addEventListener("pagehide", detach);

      return () => {
        window.removeEventListener("pagehide", detach);
        detach();
        port.close();
      };
    }

    const es = new EventSource(`/api/sse/orders/customer`);
    es.onmessage = (event) => handleData(event.data);
    return () => es.close();
  }, [enabled, queryClient]);

  return orders;
}
